import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import {
    BillableDeliveryNoteResponse,
    BillableDeliveryNoteStatus
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { store } from '@/store/store'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { AppliedBillableConceptExtraFieldsPatchInBatchRequest } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalDeliveryNotesCrudActions extends Vue {
    @Prop({ type: Object }) readonly deliveryNote!: Nullable<BillableDeliveryNoteResponse>
    @Prop({ type: Number, default: 0 }) readonly deliveryNoteConceptsCount!: number
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConceptsIds!: number[]
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConceptsToUpdate!: AppliedBillableConceptExtraFieldsPatchInBatchRequest[]

    @Emit()
    refresh(deliveryNoteId: number): number {
        return deliveryNoteId
    }

    private get _deliveryNoteGenerated(): boolean {
        return this.deliveryNote?.status.code === BillableDeliveryNoteStatus.GENERATED
    }
    private get _deliveryNoteIssued(): boolean {
        return this.deliveryNote?.status.code === BillableDeliveryNoteStatus.ISSUED
    }
    private get _deliveryNoteVerified(): boolean {
        return this.deliveryNote?.status.code === BillableDeliveryNoteStatus.VERIFIED
    }

    get visibleCreateDeliveryNote(): boolean {
        return !this.deliveryNote && this.appliedBillableConceptsIds.length > 0
    }
    get visibleUpdateDeliveryNote(): boolean {
        return this._deliveryNoteGenerated || this._deliveryNoteIssued
    }
    get visibleDeleteDeliveryNote(): boolean {
        return this._deliveryNoteGenerated
    }

    get visibleLoadData(): boolean {
        return (this._deliveryNoteIssued || this._deliveryNoteVerified) &&
            false // TODO: pending to implement
    }
    get visibleSendData(): boolean {
        return !!this.deliveryNote &&
            false // TODO: pending to implement
    }

    get visibleIssueDeliveryNote(): boolean {
        return this._deliveryNoteGenerated && this.deliveryNoteConceptsCount > 0
    }
    get visibleVerifyDeliveryNote(): boolean {
        return this._deliveryNoteIssued && this.deliveryNoteConceptsCount > 0
    }

    /// DELIVERY NOTE ///

    async onCreateDeliveryNote(): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            this.appliedBillableConceptsIds.length,
            async () => {
                const response =
                    await PromiseUtils.toCallUnique(
                        billingCommonDeliveryNoteService.create(
                            store.state.labId,
                            this.appliedBillableConceptsIds))
                if (response !== null) {
                    ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.generated'))
                    this.refresh(response.id) // emits an event
                }
            }
        )
    }

    private async _updateDeliveryNote(): Promise<boolean> {
        if (this.deliveryNote) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingCommonDeliveryNoteService.patch(
                        store.state.labId,
                        {
                            id: this.deliveryNote.id,
                            externalReference: this.deliveryNote.externalReference ?? undefined
                        }
                    ))
            return response !== null
        }
        return false
    }

    private async _updateDeliveryNoteConcepts(): Promise<boolean> {
        if (this.deliveryNote && this.appliedBillableConceptsToUpdate.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingAppliedBillableConceptService.patchWithExtraFields(
                        store.state.labId,
                        this.appliedBillableConceptsToUpdate
                    ))
            return response !== null
        }
        return false
    }
    async onUpdateDeliveryNote(): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            this.appliedBillableConceptsToUpdate.length,
            async () => {
                if (this.deliveryNote) {
                    await this._updateDeliveryNote()
                    await this._updateDeliveryNoteConcepts()
                    ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.updated'))
                    this.refresh(this.deliveryNote.id) // emits an event
                }
            }
        )
    }
    async onDeleteDeliveryNote(): Promise<void> {
        if (this.deliveryNote) {
            const { id, numberOfconcepts } = this.deliveryNote
            await BillingOperationWithConceptsGuard.run(
                numberOfconcepts,
                async () => {
                    const response =
                            await PromiseUtils.toCallSecure(
                                billingCommonDeliveryNoteService.delete(
                                    store.state.labId,
                                    id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.deleted'))
                        this.refresh(id) // emits an event
                    }
                }
            )
        }
    }

    async onIssueDeliveryNote(): Promise<void> {
        if (this.deliveryNote) {
            const { id, numberOfconcepts } = this.deliveryNote
            await BillingOperationWithConceptsGuard.run(
                numberOfconcepts,
                async () => {
                    const mustUpdate = this.appliedBillableConceptsToUpdate.length
                    let updated = false
                    if (mustUpdate > 0) {
                        updated = await this._updateDeliveryNoteConcepts()
                    }
                    if (!mustUpdate || updated) {
                        const updatedDeliveryNote = await this._updateDeliveryNote()

                        const response =
                            await PromiseUtils.toCallSecure(
                                billingCommonDeliveryNoteService.markAsIssued(
                                    store.state.labId,
                                    id))
                        if (response !== null && updatedDeliveryNote) {
                            ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.issued'))
                            this.refresh(id) // emits an event
                        }
                    }
                }
            )
        }
    }
    async onVerifyDeliveryNote(): Promise<void> {
        if (this.deliveryNote) {
            const { id, numberOfconcepts } = this.deliveryNote
            await BillingOperationWithConceptsGuard.run(
                numberOfconcepts,
                async () => {
                    const updatedDeliveryNote = await this._updateDeliveryNote()

                    const response =
                        await PromiseUtils.toCallSecure(
                            billingCommonDeliveryNoteService.markAsVerified(
                                store.state.labId,
                                id))
                    if (response !== null && updatedDeliveryNote) {
                        ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.verified'))
                        this.refresh(id) // emits an event
                    }
                }
            )
        }
    }

    /// AUTO INVOICE ///

    onLoadData(): void {
        // TODO: pendent to backend implementation
    }
    onSendData(): void {
        // TODO: pendent to backend implementation
    }
}
